import {
  IMenuQrStickerDpmm,
  IMenuQrStickerLoadOptions,
  IMenuQrStickerPadding,
  IMenuQrStickerRotation,
  IMenuQrStickerSettings,
} from '@/interfaces/IMenuQrStickerSettings';

const MENU_QR_STICKER_STORAGE_NAMESPACE = 'menu-qr-sticker-settings';
const MENU_QR_STICKER_STORAGE_VERSION = 'v1';
const LEGACY_TABLE_STICKER_STORAGE_NAMESPACE = 'table-sticker-settings';
const LEGACY_TABLE_STICKER_STORAGE_VERSION = 'v1';

export const menuQrStickerDpmmOptions: IMenuQrStickerDpmm[] = [6, 8, 12, 24];

export const menuQrStickerRotationOptions: IMenuQrStickerRotation[] = [
  0,
  90,
  180,
  270,
];

export const defaultMenuQrStickerSettings: IMenuQrStickerSettings = {
  labelWidthMm: 82,
  labelHeightMm: 82,
  dpmm: 8,
  padding: {
    top: 3,
    right: 3,
    bottom: 3,
    left: 3,
  },
  offsetXMm: 0,
  offsetYMm: 0,
  rotation: 90,
  qrScale: 0.65,
  titleFontSizeMm: 6,
  titleGapMm: 1,
  logoSizeMm: 10,
  logoOffsetMm: 3,
  footerWidthMm: 46,
  footerOffsetBottomMm: 3,
};

function cloneDefaultPadding(): IMenuQrStickerPadding {
  return {
    top: defaultMenuQrStickerSettings.padding.top,
    right: defaultMenuQrStickerSettings.padding.right,
    bottom: defaultMenuQrStickerSettings.padding.bottom,
    left: defaultMenuQrStickerSettings.padding.left,
  };
}

export function getDefaultMenuQrStickerSettings(): IMenuQrStickerSettings {
  return {
    ...defaultMenuQrStickerSettings,
    padding: cloneDefaultPadding(),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeNumber(
  value: unknown,
  fallback: number,
  {
    min,
    max,
  }: {
    min?: number;
    max?: number;
  } = {}
) {
  const normalized =
    typeof value === 'string' && value.trim() === '' ? NaN : Number(value);

  if (!Number.isFinite(normalized)) return fallback;

  let finalValue = normalized;

  if (typeof min === 'number' && finalValue < min) {
    finalValue = min;
  }

  if (typeof max === 'number' && finalValue > max) {
    finalValue = max;
  }

  return finalValue;
}

function normalizeBranchId(branchId: unknown): number | null {
  const normalized = Number(branchId);

  if (!Number.isInteger(normalized) || normalized <= 0) return null;

  return normalized;
}

function isMenuQrStickerDpmm(value: number): value is IMenuQrStickerDpmm {
  return menuQrStickerDpmmOptions.includes(value as IMenuQrStickerDpmm);
}

function isMenuQrStickerRotation(
  value: number
): value is IMenuQrStickerRotation {
  return menuQrStickerRotationOptions.includes(value as IMenuQrStickerRotation);
}

function normalizeDpmm(value: unknown): IMenuQrStickerDpmm {
  const normalized = normalizeNumber(value, defaultMenuQrStickerSettings.dpmm);

  if (isMenuQrStickerDpmm(normalized)) return normalized;

  return defaultMenuQrStickerSettings.dpmm;
}

function normalizeRotation(value: unknown): IMenuQrStickerRotation {
  const normalized = normalizeNumber(
    value,
    defaultMenuQrStickerSettings.rotation
  );

  if (isMenuQrStickerRotation(normalized)) return normalized;

  return defaultMenuQrStickerSettings.rotation;
}

function normalizePadding(value: unknown): IMenuQrStickerPadding {
  const defaults = defaultMenuQrStickerSettings.padding;
  const rawPadding = isRecord(value) ? value : {};

  return {
    top: normalizeNumber(rawPadding.top, defaults.top, { min: 0, max: 50 }),
    right: normalizeNumber(rawPadding.right, defaults.right, {
      min: 0,
      max: 50,
    }),
    bottom: normalizeNumber(rawPadding.bottom, defaults.bottom, {
      min: 0,
      max: 50,
    }),
    left: normalizeNumber(rawPadding.left, defaults.left, { min: 0, max: 50 }),
  };
}

function readSettingsByKey(storageKey: string) {
  try {
    const rawSettings = localStorage.getItem(storageKey);

    if (!rawSettings) return null;

    return normalizeMenuQrStickerSettings(JSON.parse(rawSettings));
  } catch (error) {
    console.error(error);
    return null;
  }
}

function persistSettings(settings: IMenuQrStickerSettings) {
  localStorage.setItem(
    MenuQrStickerSettings.buildStorageKey(),
    JSON.stringify(settings)
  );
}

function buildLegacyStorageKeys({
  legacyBranchId,
}: IMenuQrStickerLoadOptions = {}) {
  const keys: string[] = [];
  const branchId = normalizeBranchId(legacyBranchId);

  if (branchId !== null) {
    keys.push(
      `${LEGACY_TABLE_STICKER_STORAGE_NAMESPACE}:${LEGACY_TABLE_STICKER_STORAGE_VERSION}:${branchId}`
    );
  }

  keys.push(
    `${LEGACY_TABLE_STICKER_STORAGE_NAMESPACE}:${LEGACY_TABLE_STICKER_STORAGE_VERSION}:global`
  );

  return keys;
}

export function normalizeMenuQrStickerSettings(
  settings: unknown
): IMenuQrStickerSettings {
  const defaults = getDefaultMenuQrStickerSettings();
  const rawSettings = isRecord(settings) ? settings : {};

  return {
    labelWidthMm: normalizeNumber(
      rawSettings.labelWidthMm,
      defaults.labelWidthMm,
      {
        min: 20,
        max: 300,
      }
    ),
    labelHeightMm: normalizeNumber(
      rawSettings.labelHeightMm,
      defaults.labelHeightMm,
      {
        min: 20,
        max: 300,
      }
    ),
    dpmm: normalizeDpmm(rawSettings.dpmm),
    padding: normalizePadding(rawSettings.padding),
    offsetXMm: normalizeNumber(rawSettings.offsetXMm, defaults.offsetXMm, {
      min: -30,
      max: 30,
    }),
    offsetYMm: normalizeNumber(rawSettings.offsetYMm, defaults.offsetYMm, {
      min: -30,
      max: 30,
    }),
    rotation: normalizeRotation(rawSettings.rotation),
    qrScale: normalizeNumber(rawSettings.qrScale, defaults.qrScale, {
      min: 0.2,
      max: 1,
    }),
    titleFontSizeMm: normalizeNumber(
      rawSettings.titleFontSizeMm,
      defaults.titleFontSizeMm,
      {
        min: 2,
        max: 30,
      }
    ),
    titleGapMm: normalizeNumber(rawSettings.titleGapMm, defaults.titleGapMm, {
      min: 0,
      max: 20,
    }),
    logoSizeMm: normalizeNumber(rawSettings.logoSizeMm, defaults.logoSizeMm, {
      min: 0,
      max: 50,
    }),
    logoOffsetMm: normalizeNumber(
      rawSettings.logoOffsetMm,
      defaults.logoOffsetMm,
      {
        min: 0,
        max: 30,
      }
    ),
    footerWidthMm: normalizeNumber(
      rawSettings.footerWidthMm,
      defaults.footerWidthMm,
      {
        min: 5,
        max: 200,
      }
    ),
    footerOffsetBottomMm: normalizeNumber(
      rawSettings.footerOffsetBottomMm,
      defaults.footerOffsetBottomMm,
      {
        min: 0,
        max: 30,
      }
    ),
  };
}

export class MenuQrStickerSettings {
  static buildStorageKey() {
    return `${MENU_QR_STICKER_STORAGE_NAMESPACE}:${MENU_QR_STICKER_STORAGE_VERSION}`;
  }

  static getDefaultSettings() {
    return getDefaultMenuQrStickerSettings();
  }

  static loadSettings(options: IMenuQrStickerLoadOptions = {}) {
    const currentSettings = readSettingsByKey(this.buildStorageKey());

    if (currentSettings) return currentSettings;

    for (const legacyKey of buildLegacyStorageKeys(options)) {
      const legacySettings = readSettingsByKey(legacyKey);

      if (!legacySettings) continue;

      try {
        persistSettings(legacySettings);
      } catch (error) {
        console.error(error);
      }

      return legacySettings;
    }

    return this.getDefaultSettings();
  }

  static saveSettings(settings: IMenuQrStickerSettings) {
    const normalizedSettings = normalizeMenuQrStickerSettings(settings);

    try {
      persistSettings(normalizedSettings);
      return normalizedSettings;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
}
